import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../services/api';
import { Search, Receipt, Ban, Download, RefreshCw, ChevronDown, ChevronUp, Send, CalendarClock } from 'lucide-react';
import { formatUnidade } from '../../utils/unidade';
import BoletosErpBox from '../../components/shared/BoletosErpBox';

// download autenticado (o link direto do LinkPay exige login lá, que o usuário do CRM não tem
// — o backend proxia usando a sessão dele) — mesmo padrão de downloadDocx em ContratosList.jsx
async function baixarPdfAutenticado(url, nomeArquivo) {
  try {
    const res = await api.get(url, { responseType: 'blob' });
    const blobUrl = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = blobUrl; a.download = nomeArquivo || 'boleto.pdf';
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); URL.revokeObjectURL(blobUrl);
  } catch (err) {
    alert('Erro ao baixar o boleto: ' + (err.response?.data?.error || err.message));
  }
}

function fmtBRL(val) {
  if (val === null || val === undefined || val === '') return '—';
  return parseFloat(val).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}
function fmtData(str) {
  if (!str) return '—';
  // LinkPay manda datetime ISO completo ("2026-09-15T03:00:00.000000Z") — pega só a data
  const m = String(str).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  try { return new Date(str).toLocaleDateString('pt-BR'); } catch { return str || '—'; }
}

const STATUS_LINKPAY = {
  pending: { label: 'Pendente', cor: '#94a3b8' },
  registered: { label: 'Registrado', cor: '#3b82f6' },
  paid: { label: 'Pago', cor: '#16a34a' },
  overdue: { label: 'Vencido', cor: '#dc2626' },
  cancelled: { label: 'Cancelado', cor: '#64748b' },
  blocked: { label: 'Bloqueado', cor: '#b91c1c' },
  split: { label: 'Dividido', cor: '#8b5cf6' },
};
function Badge({ cor, children }) {
  return (
    <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 999, fontSize: 12, fontWeight: 600, color: '#fff', background: cor || '#94a3b8' }}>
      {children}
    </span>
  );
}

// ── modal de resumo antes de emitir de verdade ────────────────────────────
function ModalResumoEmissao({ contratoId, preview, isLoading, error, onClose, onEmitido }) {
  const data = preview;
  const emitir = useMutation({
    mutationFn: () => api.post(`contratos/${contratoId}/linkpay/emitir-comissao`),
    onSuccess: (r) => {
      onEmitido();
      const d = r.data.data;
      if (d && d.criadoNovo === false) alert(d.mensagem || 'Nada pendente de emissão.');
      else if (d && d.avisoEscrituracao) alert(`Boletos emitidos, mas a escrituração automática falhou: ${d.avisoEscrituracao}\n\nEscriture manualmente na venda dentro do portal da LinkPay.`);
    },
    onError: (err) => alert('Erro ao emitir no LinkPay: ' + (err.response?.data?.error || err.message)),
  });

  return (
    <div style={overlay} onClick={onClose}>
      <div style={modal} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>Resumo antes de emitir</h3>

        {isLoading && <div style={{ color: '#64748b' }}>Carregando…</div>}
        {error && <div style={{ color: '#dc2626' }}>Erro: {error.response?.data?.error || error.message}</div>}

        {data && (
          <>
            {data.avisos.length > 0 && (
              <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: '10px 14px', marginBottom: 14 }}>
                <strong style={{ color: '#dc2626', fontSize: 13 }}>Corrija antes de emitir:</strong>
                <ul style={{ margin: '6px 0 0', paddingLeft: 18, fontSize: 13, color: '#991b1b' }}>
                  {data.avisos.map((a, i) => <li key={i}>{a}</li>)}
                </ul>
              </div>
            )}

            {!data.parcelasPendentes.length ? (
              <div style={{ color: '#94a3b8', fontSize: 13 }}>Nenhuma parcela de comissão pendente de emissão.</div>
            ) : (
              <>
                <div style={{ fontSize: 13, color: '#64748b', marginBottom: 8 }}>Código da venda no LinkPay: <code>{data.salesCode}</code></div>
                <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ textAlign: 'left', color: '#64748b' }}>
                      <th style={th}>Vencimento</th>
                      <th style={th}>Valor</th>
                      <th style={th}>Split</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.parcelasPendentes.map((p) => (
                      <tr key={p.parcelaId} style={{ borderTop: '1px solid #eee' }}>
                        <td style={td}>{fmtData(p.vencimento)}</td>
                        <td style={td}>{fmtBRL(p.valor)}</td>
                        <td style={td}>{p.split.map((s) => `${s.beneficiario}: ${fmtBRL(s.valor)}`).join(' + ')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
                <div style={{ textAlign: 'right', fontWeight: 600, marginTop: 8, fontSize: 13 }}>
                  Total a emitir: {fmtBRL(data.totalPendente)}
                </div>
              </>
            )}

            {!!data.parcelasJaEmitidas.length && (
              <details style={{ marginTop: 14, fontSize: 12, color: '#94a3b8' }}>
                <summary style={{ cursor: 'pointer' }}>{data.parcelasJaEmitidas.length} parcela(s) já emitida(s) antes (não serão duplicadas)</summary>
                <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                  {data.parcelasJaEmitidas.map((p) => (
                    <li key={p.parcelaId}>{fmtData(p.vencimento)} — {fmtBRL(p.valor)} (boleto #{p.bankSlipId})</li>
                  ))}
                </ul>
              </details>
            )}
          </>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
          <button onClick={onClose} style={{ ...btnIcon, width: 'auto', padding: '6px 14px' }}>Cancelar</button>
          <button
            disabled={!data || !data.podeEmitir || emitir.isPending}
            onClick={() => emitir.mutate()}
            style={{ ...btnPrimary, opacity: (!data || !data.podeEmitir) ? 0.5 : 1 }}
          >
            <Send size={13} /> {emitir.isPending ? 'Emitindo…' : 'Confirmar emissão'}
          </button>
        </div>
      </div>
    </div>
  );
}

function hojeSP() {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' });
}

// ── modal de reemissão: cancela o boleto atual e gera outro com vencimento novo ──
function ModalReemitir({ contratoId, boleto, onClose, onReemitido }) {
  const [vencimento, setVencimento] = useState(hojeSP());
  const reemitir = useMutation({
    mutationFn: () => api.post(`contratos/${contratoId}/linkpay/boletos/${boleto.bankSlipId}/reemitir`, { vencimento }),
    onSuccess: (r) => {
      const d = r.data.data;
      if (d && d.avisoEscrituracao) alert(`Boleto reemitido, mas a escrituração automática falhou: ${d.avisoEscrituracao}\n\nEscriture manualmente na venda dentro do portal da LinkPay.`);
      onReemitido();
    },
    onError: (err) => { alert('Erro ao reemitir no LinkPay: ' + (err.response?.data?.error || err.message)); onReemitido(); },
  });

  return (
    <div style={overlay} onClick={reemitir.isPending ? undefined : onClose}>
      <div style={{ ...modal, maxWidth: 420 }} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>Reemitir boleto</h3>
        <div style={{ fontSize: 13, color: '#334155', lineHeight: 1.6 }}>
          Protocolo <code>{boleto.protocolo}</code> · {fmtBRL(boleto.valor)} · venc. {fmtData(boleto.vencimento)}
        </div>
        <div style={{ fontSize: 13, color: '#64748b', margin: '10px 0 14px' }}>
          {boleto.status === 'cancelled' ? <>O boleto atual já está <strong>cancelado</strong>;</> : <>O boleto atual será <strong>cancelado</strong> no LinkPay e</>} um novo, com o mesmo valor e split, será emitido com o vencimento abaixo.
        </div>
        <label style={{ fontSize: 13, fontWeight: 600 }}>
          Novo vencimento
          <input
            type="date"
            value={vencimento}
            min={hojeSP()}
            onChange={(e) => setVencimento(e.target.value)}
            style={{ display: 'block', marginTop: 6, padding: '6px 8px', border: '1px solid #e5e7eb', borderRadius: 6 }}
          />
        </label>
        {reemitir.isPending && (
          <div style={{ fontSize: 12, color: '#64748b', marginTop: 12 }}>Cancelando e emitindo… pode levar até 1 minuto.</div>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
          <button onClick={onClose} disabled={reemitir.isPending} style={{ ...btnIcon, width: 'auto', padding: '6px 14px' }}>Voltar</button>
          <button
            disabled={!vencimento || vencimento < hojeSP() || reemitir.isPending}
            onClick={() => reemitir.mutate()}
            style={{ ...btnPrimary, opacity: (!vencimento || vencimento < hojeSP()) ? 0.5 : 1 }}
          >
            <CalendarClock size={13} /> {reemitir.isPending ? 'Reemitindo…' : 'Cancelar e reemitir'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── boletos de comissão (LinkPay, ao vivo) ────────────────────────────────
function BoletosComissao({ contratoId }) {
  const qc = useQueryClient();
  const [mostrarResumo, setMostrarResumo] = useState(false);
  const [reemitindo, setReemitindo] = useState(null);
  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['linkpay-boletos', contratoId],
    queryFn: () => api.get(`contratos/${contratoId}/linkpay/boletos`).then((r) => r.data.data),
  });
  // resumo do que falta emitir — busca só localmente no CRM, sem tocar o LinkPay (rápido) —
  // controla se o botão "Emitir" fica ativo, além de alimentar o modal de confirmação
  const { data: preview, isLoading: previewLoading, error: previewError } = useQuery({
    queryKey: ['linkpay-preview', contratoId],
    queryFn: () => api.get(`contratos/${contratoId}/linkpay/preview-emissao`).then((r) => r.data.data),
  });
  const temPendente = !!preview?.parcelasPendentes?.length;

  const cancelar = useMutation({
    mutationFn: (bankSlipId) => api.post(`contratos/${contratoId}/linkpay/boletos/${bankSlipId}/cancelar`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['linkpay-boletos', contratoId] }),
    onError: (err) => alert('Erro ao cancelar no LinkPay: ' + (err.response?.data?.error || err.message)),
  });

  if (isLoading) return <div style={{ padding: 12, color: '#64748b', fontSize: 13 }}>Carregando…</div>;
  if (error) return <div style={{ padding: 12, color: '#dc2626', fontSize: 13 }}>Erro: {error.response?.data?.error || error.message}</div>;

  return (
    <div style={{ padding: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <strong style={{ fontSize: 14 }}>Comissão (LinkPay)</strong>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => refetch()} disabled={isFetching} title="Atualizar" style={btnIcon}>
            <RefreshCw size={14} className={isFetching ? 'spin' : ''} />
          </button>
          <button
            onClick={() => setMostrarResumo(true)}
            disabled={!previewLoading && !temPendente}
            title={!previewLoading && !temPendente ? 'Não há boletos de comissão pendentes de emissão' : ''}
            style={{ ...btnPrimary, opacity: (!previewLoading && !temPendente) ? 0.5 : 1, cursor: (!previewLoading && !temPendente) ? 'default' : 'pointer' }}
          >
            <Send size={13} /> {data?.emitido ? 'Emitir pendentes' : 'Emitir boletos de comissão'}
          </button>
        </div>
      </div>

      {mostrarResumo && (
        <ModalResumoEmissao
          contratoId={contratoId}
          preview={preview}
          isLoading={previewLoading}
          error={previewError}
          onClose={() => setMostrarResumo(false)}
          onEmitido={() => {
            setMostrarResumo(false);
            qc.invalidateQueries({ queryKey: ['linkpay-boletos', contratoId] });
            qc.invalidateQueries({ queryKey: ['linkpay-preview', contratoId] });
          }}
        />
      )}

      {reemitindo && (
        <ModalReemitir
          contratoId={contratoId}
          boleto={reemitindo}
          onClose={() => setReemitindo(null)}
          onReemitido={() => {
            setReemitindo(null);
            qc.invalidateQueries({ queryKey: ['linkpay-boletos', contratoId] });
            qc.invalidateQueries({ queryKey: ['linkpay-preview', contratoId] });
          }}
        />
      )}

      {!data?.boletos?.length ? (
        <div style={{ color: '#94a3b8', fontSize: 13 }}>Nenhum boleto de comissão emitido ainda.</div>
      ) : (
        <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ textAlign: 'left', color: '#64748b' }}>
              <th style={th}>Protocolo</th>
              <th style={th}>Vencimento</th>
              <th style={th}>Valor</th>
              <th style={th}>Split</th>
              <th style={th}>Status</th>
              <th style={th}>Pago em</th>
              <th style={th}></th>
            </tr>
          </thead>
          <tbody>
            {data.boletos.map((b) => {
              const st = STATUS_LINKPAY[b.status] || { label: b.status, cor: '#94a3b8' };
              return (
                <tr key={b.bankSlipId} style={{ borderTop: '1px solid #eee' }}>
                  <td style={td}><code>{b.protocolo}</code></td>
                  <td style={td}>{fmtData(b.vencimento)}</td>
                  <td style={td}>{fmtBRL(b.valor)}</td>
                  <td style={td}>{(b.split || []).map((s) => `${s.beneficiario || '—'}: ${fmtBRL(s.valor)}`).join(' + ')}</td>
                  <td style={td}>
                    <Badge cor={st.cor}>{st.label}</Badge>
                    {b.pagoSoNoBanco && <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }} title="O banco já registrou o pagamento; o LinkPay ainda não atualizou">confirmado pelo banco</div>}
                  </td>
                  <td style={td}>{fmtData(b.pagoEm)}</td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>
                    <button
                      title="Baixar PDF"
                      onClick={() => baixarPdfAutenticado(`contratos/${contratoId}/linkpay/boletos/${b.bankSlipId}/download`, `boleto-${b.protocolo}.pdf`)}
                      style={btnIcon}
                    >
                      <Download size={14} />
                    </button>
                    {b.podeReemitir && (
                      <button
                        title={b.status === 'overdue' ? 'Boleto vencido — reemitir com novo vencimento' : 'Reemitir com novo vencimento'}
                        onClick={() => setReemitindo(b)}
                        style={{ ...btnIcon, marginLeft: 6, ...(b.status === 'overdue' ? { color: '#fff', background: '#dc2626', borderColor: '#dc2626' } : {}) }}
                      >
                        <CalendarClock size={14} />
                      </button>
                    )}
                    {b.podeCancelar && (
                      <button
                        title="Cancelar boleto"
                        onClick={() => { if (window.confirm(`Cancelar o boleto ${b.protocolo} (${fmtBRL(b.valor)})? Essa ação é feita direto no banco, via LinkPay.`)) cancelar.mutate(b.bankSlipId); }}
                        disabled={cancelar.isPending}
                        style={{ ...btnIcon, color: '#dc2626', marginLeft: 6 }}
                      >
                        <Ban size={14} />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      )}
    </div>
  );
}

// ── boletos do cliente lançados pelo financeiro no ERP (ao vivo, com o PDF) ──
function BoletosErp({ contratoId }) {
  return (
    <div style={{ padding: 12 }}>
      <strong style={{ fontSize: 14 }}>Boletos do cliente (financeiro / ERP)</strong>
      <div style={{ marginTop: 8 }}><BoletosErpBox baseUrl={`contratos/${contratoId}`} compacto /></div>
    </div>
  );
}

function LinhaContrato({ c }) {
  const [aberto, setAberto] = useState(false);
  return (
    <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, marginBottom: 10, overflow: 'hidden' }}>
      <div
        onClick={() => setAberto((v) => !v)}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '10px 14px', cursor: 'pointer', background: '#fafafa' }}
      >
        <div style={{ minWidth: 0 }}>
          <strong>{c.cliente_nome || '—'}</strong>
          <span style={{ color: '#64748b', marginLeft: 10, fontSize: 13 }}>
            {c.empreendimento_nome} · {formatUnidade(c.unidade_numero, c.torre)} · {fmtBRL(c.valor_total)}
          </span>
          {c.status !== 'assinado' && (
            <span style={{ marginLeft: 10 }}><Badge cor="#f59e0b">Aguardando assinatura</Badge></span>
          )}
        </div>
        {aberto ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
      </div>
      {aberto && (
        // no celular as duas colunas empilham (min 380px cada); a linha divisória é o gap de 1px
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 380px), 1fr))', gap: 1, background: '#e5e7eb', borderTop: '1px solid #e5e7eb' }}>
          <div style={{ background: '#fff', minWidth: 0 }}><BoletosComissao contratoId={c.id} /></div>
          <div style={{ background: '#fff', minWidth: 0 }}><BoletosErp contratoId={c.id} /></div>
        </div>
      )}
    </div>
  );
}

export default function BoletosList() {
  const [search, setSearch] = useState('');
  const { data, isLoading } = useQuery({
    queryKey: ['contratos-boletos'],
    queryFn: () => api.get('/contratos').then((r) => r.data.data),
  });

  // assinados + os que já estão em assinatura no Clicksign (envelope rodando) ou já têm boleto
  // de comissão emitido no LinkPay — a comissão costuma vencer antes do contrato ser assinado.
  // Envelopes com prazo vencido/cancelados sem boleto ficam de fora.
  const contratosComBoleto = (data || []).filter((c) => c.status === 'assinado'
    || (c.status === 'enviado' && c.clicksign_status === 'running')
    || !!c.linkpay_enterprise_order_id);
  const filtrados = search
    ? contratosComBoleto.filter((c) => (c.cliente_nome || '').toLowerCase().includes(search.toLowerCase()) || (c.cliente_cpf || '').includes(search))
    : contratosComBoleto;

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <Receipt size={22} />
        <h1 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>Boletos</h1>
      </div>
      <p style={{ color: '#64748b', fontSize: 13, marginTop: 0, marginBottom: 16 }}>
        Comissão via LinkPay (emitir/consultar/reemitir/cancelar direto por aqui) e boletos do cliente lançados pelo financeiro no ERP (com o PDF).
      </p>

      <div style={{ position: 'relative', maxWidth: 360, marginBottom: 16 }}>
        <Search size={16} style={{ position: 'absolute', left: 10, top: 10, color: '#94a3b8' }} />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar por cliente ou CPF…"
          style={{ width: '100%', padding: '8px 8px 8px 32px', border: '1px solid #e5e7eb', borderRadius: 6 }}
        />
      </div>

      {isLoading ? (
        <div style={{ color: '#64748b' }}>Carregando contratos…</div>
      ) : !filtrados.length ? (
        <div style={{ color: '#94a3b8' }}>Nenhum contrato assinado ou em assinatura encontrado.</div>
      ) : (
        filtrados.map((c) => <LinhaContrato key={c.id} c={c} />)
      )}
    </div>
  );
}

const th = { padding: '4px 8px', fontWeight: 600, fontSize: 12 };
const td = { padding: '6px 8px' };
const btnIcon = { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, border: '1px solid #e5e7eb', borderRadius: 6, background: '#fff', cursor: 'pointer' };
const btnPrimary = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', border: 'none', borderRadius: 6, background: '#111827', color: '#fff', fontSize: 13, cursor: 'pointer' };
const overlay = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 };
const modal = { background: '#fff', borderRadius: 10, padding: 20, width: '100%', maxWidth: 560, maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.2)' };
